import { useEffect, useState, useRef, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  Grid,
  FormHelperText,
  Textarea,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Divider,
  Spinner,
} from '@chakra-ui/react'
import { Plus, Edit, Trash2 } from 'lucide-react'
import {
  createFieldEngineerSchema,
  updateFieldEngineerSchema,
  createFieldEngineerCertificationSchema,
  updateFieldEngineerCertificationSchema,
  type CreateFieldEngineerFormData,
  type UpdateFieldEngineerFormData,
  type CreateFieldEngineerCertificationFormData,
} from '@/lib/validations/auth'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'

interface User {
  id: number
  user_login: string
  user_email: string
  first_name?: string
  last_name?: string
  employee_id?: string
  position?: string
  area_cluster_id?: number | null
}

const formatSelectedUserLabel = (user: User): string => {
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
  const identity = user.employee_id || fullName || user.user_login || user.user_email || 'Unknown User'
  const roleOrContact = user.position || user.user_email || user.user_login || 'N/A'
  return `${identity} (${roleOrContact})`
}

interface Certification {
  id: number
  certification_name: string
  certification_type: string | null
  issued_date: string | null
  expiry_date: string | null
  status: 'ACTIVE' | 'EXPIRED' | 'PENDING'
  issuing_organization: string | null
  certificate_number: string | null
  notes: string | null
}

interface FieldEngineerFormProps {
  fieldEngineerId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

export function FieldEngineerForm({ fieldEngineerId, onSuccess, onCancel }: FieldEngineerFormProps) {
  const colors = useThemeColors()
  const [users, setUsers] = useState<User[]>([])
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [certifications, setCertifications] = useState<Certification[]>([])
  const [isLoadingCertifications, setIsLoadingCertifications] = useState(false)
  const { showError, showSuccess } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [editingCertification, setEditingCertification] = useState<Certification | null>(null)
  const isEditMode = !!fieldEngineerId

  // User search states
  const [userSearchTerm, setUserSearchTerm] = useState('')
  const [userSearchResults, setUserSearchResults] = useState<User[]>([])
  const [isUserSearching, setIsUserSearching] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const userSearchInputRef = useRef<HTMLInputElement>(null)
  const userSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    watch,
    reset,
  } = useForm<CreateFieldEngineerFormData | UpdateFieldEngineerFormData>({
    resolver: zodResolver(isEditMode ? updateFieldEngineerSchema : createFieldEngineerSchema),
    defaultValues: {
      user_id: null,
      full_name: '',
      cluster: '',
      province: null,
      region: null,
      launch_point: '',
      address: '',
      normalized_address: null,
      longitude: null,
      latitude: null,
      launch_longitude: null,
      launch_latitude: null,
      comments: null,
    },
  })

  const {
    register: registerCert,
    handleSubmit: handleSubmitCert,
    formState: { errors: errorsCert },
    setValue: setValueCert,
    watch: watchCert,
    reset: resetCert,
  } = useForm<CreateFieldEngineerCertificationFormData>({
    resolver: zodResolver(
      editingCertification ? updateFieldEngineerCertificationSchema : createFieldEngineerCertificationSchema
    ),
    defaultValues: {
      certification_name: '',
      certification_type: null,
      issued_date: null,
      expiry_date: null,
      status: 'ACTIVE',
      issuing_organization: null,
      certificate_number: null,
      notes: null,
    },
  })

  // Debounced user search function
  const searchUsers = useCallback(async (searchTerm: string) => {
    if (searchTerm.length < 3) {
      setUserSearchResults([])
      return
    }

    setIsUserSearching(true)
    try {
      const response = await apiClient.get(`/options/users/search?search=${encodeURIComponent(searchTerm)}`)
      const usersData = response.data?.data || response.data || []
      // Filter for Field Engineer users only
      const fieldEngineerUsers = Array.isArray(usersData)
        ? usersData.filter((user: User) => {
          // Check if user has Field Engineer role (you may need to adjust this based on your data structure)
          return true // For now, return all users from search
        })
        : []
      setUserSearchResults(fieldEngineerUsers)
    } catch (error: any) {
      showError(error)
      setUserSearchResults([])
    } finally {
      setIsUserSearching(false)
    }
  }, [showError])

  // Handle user search input change
  const handleUserSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setUserSearchTerm(value)
    setSelectedUser(null)
    setValue('user_id', null, { shouldValidate: true })

    // Clear previous timeout
    if (userSearchTimeoutRef.current) {
      clearTimeout(userSearchTimeoutRef.current)
    }

    // Set new timeout for debounced search
    userSearchTimeoutRef.current = setTimeout(() => {
      searchUsers(value)
    }, 300)
  }

  // Handle user selection
  const handleUserSelect = (user: User) => {
    setSelectedUser(user)
    setValue('user_id', user.id, { shouldValidate: true })
    clearErrors('user_id')
    setUserSearchTerm(formatSelectedUserLabel(user))
    setUserSearchResults([])

    // Auto-fill full_name if first_name and last_name are available
    if (user.first_name && user.last_name) {
      const fullName = `${user.first_name} ${user.last_name}`.trim()
      setValue('full_name', fullName)
    }
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (userSearchTimeoutRef.current) {
        clearTimeout(userSearchTimeoutRef.current)
      }
    }
  }, [])

  // Fetch Field Engineer data in edit mode
  useEffect(() => {
    if (isEditMode && fieldEngineerId) {
      const fetchFieldEngineer = async () => {
        setIsLoading(true)
        try {
          const response = await apiClient.get(`/organization/field-engineers/${fieldEngineerId}`)
          const fe = response.data?.data || response.data || {}

          reset({
            user_id: fe.user_id || null,
            full_name: fe.full_name || '',
            cluster: fe.cluster || '',
            province: fe.province || null,
            region: fe.region || null,
            launch_point: fe.launch_point || '',
            address: fe.address || '',
            normalized_address: fe.normalized_address || null,
            longitude: fe.longitude ? Number(fe.longitude) : null,
            latitude: fe.latitude ? Number(fe.latitude) : null,
            launch_longitude: fe.launch_longitude ? Number(fe.launch_longitude) : null,
            launch_latitude: fe.launch_latitude ? Number(fe.launch_latitude) : null,
            comments: fe.comments || null,
          })

          // Set selected user and search term if user_id exists
          if (fe.user_id && fe.user) {
            const user = fe.user as User
            setSelectedUser(user)
            setUserSearchTerm(formatSelectedUserLabel(user))
          }

          // Fetch certifications
          await fetchCertifications()
        } catch (error: any) {
          showError(error)
        } finally {
          setIsLoading(false)
        }
      }
      fetchFieldEngineer()
    }
  }, [fieldEngineerId, isEditMode, reset, showError])

  const fetchCertifications = async () => {
    if (!fieldEngineerId) return
    setIsLoadingCertifications(true)
    try {
      const response = await apiClient.get(`/organization/field-engineers/${fieldEngineerId}/certifications`)
      // Handle different response formats
      const certsData = response.data?.data || response.data || []
      setCertifications(Array.isArray(certsData) ? certsData : [])
    } catch (error: any) {
      // Don't show error if it's just a 404 (no certifications yet)
      if (error?.response?.status !== 404) {
        showError(error)
      } else {
        setCertifications([])
      }
    } finally {
      setIsLoadingCertifications(false)
    }
  }

  const onSubmit = async (data: CreateFieldEngineerFormData | UpdateFieldEngineerFormData) => {
    if (isEditMode && !data.user_id) {
      setError('user_id', { type: 'manual', message: 'User is required when editing a field engineer' })
      return
    }

    setIsSubmitting(true)
    try {
      const payload = {
        ...data,
        user_id: data.user_id || null,
        province: data.province || null,
        region: data.region || null,
        normalized_address: data.normalized_address || null,
        longitude: data.longitude || null,
        latitude: data.latitude || null,
        launch_longitude: data.launch_longitude || null,
        launch_latitude: data.launch_latitude || null,
        comments: data.comments || null,
      }

      if (isEditMode && fieldEngineerId) {
        await apiClient.put(`/organization/field-engineers/${fieldEngineerId}`, payload)
        showSuccess('Success', 'Field Engineer updated successfully')
      } else {
        const response = await apiClient.post('/organization/field-engineers', payload)
        const newId = response.data?.data?.id || response.data?.id
        if (newId && onSuccess) {
          // If creating new, navigate to edit page to manage certifications
          window.location.href = `/organization/field-engineers/${newId}`
          return
        }
        showSuccess('Success', 'Field Engineer created successfully')
      }

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddCertification = () => {
    setEditingCertification(null)
    resetCert({
      certification_name: '',
      certification_type: null,
      issued_date: null,
      expiry_date: null,
      status: 'ACTIVE',
      issuing_organization: null,
      certificate_number: null,
      notes: null,
    })
    onOpen()
  }

  const handleEditCertification = (cert: Certification) => {
    setEditingCertification(cert)
    resetCert({
      certification_name: cert.certification_name,
      certification_type: cert.certification_type || null,
      issued_date: cert.issued_date || null,
      expiry_date: cert.expiry_date || null,
      status: cert.status,
      issuing_organization: cert.issuing_organization || null,
      certificate_number: cert.certificate_number || null,
      notes: cert.notes || null,
    })
    onOpen()
  }

  const handleDeleteCertification = async (certId: number) => {
    if (!fieldEngineerId) return
    if (!confirm('Are you sure you want to delete this certification?')) return

    try {
      await apiClient.delete(`/organization/field-engineers/${fieldEngineerId}/certifications/${certId}`)
      showSuccess('Success', 'Certification deleted successfully')
      await fetchCertifications()
    } catch (error: any) {
      showError(error)
    }
  }

  const onSubmitCertification = async (data: CreateFieldEngineerCertificationFormData) => {
    if (!fieldEngineerId) {
      showError('Please save the Field Engineer first before adding certifications')
      return
    }

    try {
      if (editingCertification) {
        await apiClient.put(`/organization/field-engineers/${fieldEngineerId}/certifications/${editingCertification.id}`, data)
        showSuccess('Success', 'Certification updated successfully')
      } else {
        await apiClient.post(`/organization/field-engineers/${fieldEngineerId}/certifications`, data)
        showSuccess('Success', 'Certification added successfully')
      }
      onClose()
      await fetchCertifications()
      resetCert()
    } catch (error: any) {
      showError(error)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'green'
      case 'EXPIRED':
        return 'red'
      case 'PENDING':
        return 'yellow'
      default:
        return 'gray'
    }
  }

  if (isLoading) {
    return (
      <Box p={4}>
        <Text>Loading Field Engineer data...</Text>
      </Box>
    )
  }

  return (
    <Box>
      <Box as="form" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <VStack spacing={6} align="stretch">
          <Box>
            <Text fontSize="lg" fontWeight="bold" mb={4}>
              Basic Information
            </Text>
            <VStack spacing={4} align="stretch">
              <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                <FormLabel pt={2} fontWeight="medium">
                  User {isEditMode && <Text as="span" color="red.500">*</Text>}
                </FormLabel>
                <FormControl isInvalid={!!errors.user_id}>
                  <input type="hidden" {...register('user_id', { valueAsNumber: true })} />
                  <Box position="relative">
                    <Input
                      ref={userSearchInputRef}
                      placeholder="Search field engineer..."
                      value={userSearchTerm}
                      onChange={handleUserSearchChange}
                      onFocus={() => {
                        if (userSearchTerm.length >= 3 && userSearchResults.length === 0) {
                          searchUsers(userSearchTerm)
                        }
                      }}
                      autoComplete="off"
                    />
                    {isUserSearching && (
                      <Box
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        mt={1}
                        p={2}
                        zIndex={1000}
                        boxShadow="md"
                      >
                        <HStack justify="center">
                          <Spinner size="sm" />
                          <Text fontSize="sm">Searching...</Text>
                        </HStack>
                      </Box>
                    )}
                    {userSearchResults.length > 0 && !isUserSearching && (
                      <Box
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        mt={1}
                        maxH="300px"
                        overflowY="auto"
                        zIndex={1000}
                        boxShadow="md"
                      >
                        {userSearchResults.map((user) => (
                          <Box
                            key={user.id}
                            p={2}
                            cursor="pointer"
                            _hover={{ bg: 'gray.100' }}
                            onClick={() => handleUserSelect(user)}
                            borderBottom="1px solid"
                            borderColor="gray.100"
                            _last={{ borderBottom: 'none' }}
                          >
                            <Text fontSize="sm">
                              {user.employee_id || ''} - {user.first_name || ''} {user.last_name || ''} (
                              {user.position || 'N/A'})
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {user.user_email}
                            </Text>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  <FormHelperText>
                    Type at least 3 characters to search for a field engineer {isEditMode ? '(required)' : '(optional)'}
                  </FormHelperText>
                  {selectedUser ? (
                    <FormHelperText color={selectedUser.area_cluster_id ? 'green.600' : 'orange.600'}>
                      {selectedUser.area_cluster_id
                        ? 'Area cluster is configured in User Management and will be used by FE recommendations.'
                        : 'No area cluster is configured for this user. Update the user in User Management before using automatic FE recommendations.'}
                    </FormHelperText>
                  ) : null}
                  <FormErrorMessage>{errors.user_id?.message}</FormErrorMessage>
                </FormControl>
              </Grid>

              <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                <FormLabel pt={2} fontWeight="medium">
                  Full Name <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <FormControl isInvalid={!!errors.full_name}>
                  <Input {...register('full_name')} placeholder="e.g., Alfonso Dizon" autoComplete="off" />
                  <FormErrorMessage>{errors.full_name?.message}</FormErrorMessage>
                </FormControl>
              </Grid>

              <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                <FormLabel pt={2} fontWeight="medium">Province</FormLabel>
                <FormControl isInvalid={!!errors.province}>
                  <Input {...register('province')} placeholder="e.g., MISAMIS ORIENTAL" autoComplete="off" />
                  <FormErrorMessage>{errors.province?.message}</FormErrorMessage>
                </FormControl>
              </Grid>

              <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                <FormLabel pt={2} fontWeight="medium">Region</FormLabel>
                <FormControl isInvalid={!!errors.region}>
                  <Input {...register('region')} placeholder="e.g., 10" autoComplete="off" />
                  <FormErrorMessage>{errors.region?.message}</FormErrorMessage>
                </FormControl>
              </Grid>

              <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                <FormLabel pt={2} fontWeight="medium">
                  Launch Point <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <FormControl isInvalid={!!errors.launch_point}>
                  <Input {...register('launch_point')} placeholder="e.g., CAGAYAN DE ORO CITY" autoComplete="off" />
                  <FormErrorMessage>{errors.launch_point?.message}</FormErrorMessage>
                </FormControl>
              </Grid>

              <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                <FormLabel pt={2} fontWeight="medium">
                  Address <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <FormControl isInvalid={!!errors.address}>
                  <Textarea {...register('address')} placeholder="Full address" rows={3} autoComplete="off" />
                  <FormErrorMessage>{errors.address?.message}</FormErrorMessage>
                </FormControl>
              </Grid>

              <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                <FormLabel pt={2} fontWeight="medium">Normalized Address</FormLabel>
                <FormControl isInvalid={!!errors.normalized_address}>
                  <Textarea {...register('normalized_address')} placeholder="Normalized address" rows={2} autoComplete="off" />
                  <FormErrorMessage>{errors.normalized_address?.message}</FormErrorMessage>
                </FormControl>
              </Grid>
            </VStack>
          </Box>

          <Divider />

          <Box>
            <Text fontSize="lg" fontWeight="bold" mb={4}>
              Location Coordinates
            </Text>
            <VStack spacing={4} align="stretch">
              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                <FormControl isInvalid={!!errors.longitude}>
                  <FormLabel>Longitude</FormLabel>
                  <Input
                    type="number"
                    step="any"
                    {...register('longitude', { valueAsNumber: true })}
                    placeholder="e.g., 124.6475"
                    autoComplete="off"
                  />
                  <FormErrorMessage>{errors.longitude?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.latitude}>
                  <FormLabel>Latitude</FormLabel>
                  <Input
                    type="number"
                    step="any"
                    {...register('latitude', { valueAsNumber: true })}
                    placeholder="e.g., 8.4809"
                    autoComplete="off"
                  />
                  <FormErrorMessage>{errors.latitude?.message}</FormErrorMessage>
                </FormControl>
              </Grid>

              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                <FormControl isInvalid={!!errors.launch_longitude}>
                  <FormLabel>Launch Longitude</FormLabel>
                  <Input
                    type="number"
                    step="any"
                    {...register('launch_longitude', { valueAsNumber: true })}
                    placeholder="e.g., 124.6475"
                    autoComplete="off"
                  />
                  <FormErrorMessage>{errors.launch_longitude?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.launch_latitude}>
                  <FormLabel>Launch Latitude</FormLabel>
                  <Input
                    type="number"
                    step="any"
                    {...register('launch_latitude', { valueAsNumber: true })}
                    placeholder="e.g., 8.4809"
                    autoComplete="off"
                  />
                  <FormErrorMessage>{errors.launch_latitude?.message}</FormErrorMessage>
                </FormControl>
              </Grid>
            </VStack>
          </Box>

          <Divider />

          <Box>
            <Text fontSize="lg" fontWeight="bold" mb={4}>
              Additional Information
            </Text>
            <VStack spacing={4} align="stretch">
              <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
                <FormLabel pt={2} fontWeight="medium">Comments</FormLabel>
                <FormControl isInvalid={!!errors.comments}>
                  <Textarea {...register('comments')} placeholder="Comments/Remarks" rows={3} autoComplete="off" />
                  <FormErrorMessage>{errors.comments?.message}</FormErrorMessage>
                </FormControl>
              </Grid>
            </VStack>
          </Box>

          {isEditMode && (
            <>
              <Divider />
              <Box>
                <HStack justify="space-between" mb={4}>
                  <Text fontSize="lg" fontWeight="bold">
                    Certifications
                  </Text>
                  <Button
                    leftIcon={<Plus size={16} />}
                    size="sm"
                    colorScheme="blue"
                    onClick={handleAddCertification}
                  >
                    Add Certification
                  </Button>
                </HStack>

                {isLoadingCertifications ? (
                  <Text>Loading certifications...</Text>
                ) : certifications.length === 0 ? (
                  <Text color={colors.text} fontSize="sm">
                    No certifications added yet. Click "Add Certification" to add one.
                  </Text>
                ) : (
                  <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Certification Name</Th>
                          <Th>Type</Th>
                          <Th>Issued Date</Th>
                          <Th>Expiry Date</Th>
                          <Th>Status</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {certifications.map((cert) => (
                          <Tr key={cert.id}>
                            <Td>{cert.certification_name}</Td>
                            <Td>{cert.certification_type || '-'}</Td>
                            <Td>{cert.issued_date || '-'}</Td>
                            <Td>{cert.expiry_date || '-'}</Td>
                            <Td>
                              <Badge colorScheme={getStatusColor(cert.status)}>{cert.status}</Badge>
                            </Td>
                            <Td>
                              <HStack spacing={2}>
                                <IconButton
                                  aria-label="Edit certification"
                                  icon={<Edit size={14} />}
                                  size="xs"
                                  onClick={() => handleEditCertification(cert)}
                                />
                                <IconButton
                                  aria-label="Delete certification"
                                  icon={<Trash2 size={14} />}
                                  size="xs"
                                  colorScheme="red"
                                  onClick={() => handleDeleteCertification(cert.id)}
                                />
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </Box>
            </>
          )}

          <HStack spacing={4} justify="flex-end" pt={4}>
            {onCancel && (
              <Button variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
                Cancel
              </Button>
            )}
            <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText={isEditMode ? 'Saving...' : 'Creating...'}>
              {isEditMode ? 'Save' : 'Create Field Engineer'}
            </Button>
          </HStack>
        </VStack>
      </Box>

      {/* Certification Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingCertification ? 'Edit Certification' : 'Add Certification'}</ModalHeader>
          <Box as="form" onSubmit={handleSubmitCert(onSubmitCertification)} autoComplete="off">
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <FormControl isInvalid={!!errorsCert.certification_name}>
                  <FormLabel>
                    Certification Name <Text as="span" color="red.500">*</Text>
                  </FormLabel>
                  <Input {...registerCert('certification_name')} placeholder="e.g., LENOVO Certified" autoComplete="off" />
                  <FormErrorMessage>{errorsCert.certification_name?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errorsCert.certification_type}>
                  <FormLabel>Certification Type</FormLabel>
                  <Input {...registerCert('certification_type')} placeholder="e.g., Hardware" autoComplete="off" />
                  <FormErrorMessage>{errorsCert.certification_type?.message}</FormErrorMessage>
                </FormControl>

                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                  <FormControl isInvalid={!!errorsCert.issued_date}>
                    <FormLabel>Issued Date</FormLabel>
                    <Input type="date" {...registerCert('issued_date')} autoComplete="off" />
                    <FormErrorMessage>{errorsCert.issued_date?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errorsCert.expiry_date}>
                    <FormLabel>Expiry Date</FormLabel>
                    <Input type="date" {...registerCert('expiry_date')} autoComplete="off" />
                    <FormErrorMessage>{errorsCert.expiry_date?.message}</FormErrorMessage>
                  </FormControl>
                </Grid>

                <FormControl isInvalid={!!errorsCert.status}>
                  <FormLabel>
                    Status <Text as="span" color="red.500">*</Text>
                  </FormLabel>
                  <Select {...registerCert('status')} autoComplete="off">
                    <option value="ACTIVE">Active</option>
                    <option value="EXPIRED">Expired</option>
                    <option value="PENDING">Pending</option>
                  </Select>
                  <FormErrorMessage>{errorsCert.status?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errorsCert.issuing_organization}>
                  <FormLabel>Issuing Organization</FormLabel>
                  <Input {...registerCert('issuing_organization')} placeholder="e.g., LENOVO" autoComplete="off" />
                  <FormErrorMessage>{errorsCert.issuing_organization?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errorsCert.certificate_number}>
                  <FormLabel>Certificate Number</FormLabel>
                  <Input {...registerCert('certificate_number')} placeholder="Certificate reference number" autoComplete="off" />
                  <FormErrorMessage>{errorsCert.certificate_number?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errorsCert.notes}>
                  <FormLabel>Notes</FormLabel>
                  <Textarea {...registerCert('notes')} placeholder="Additional notes" rows={3} autoComplete="off" />
                  <FormErrorMessage>{errorsCert.notes?.message}</FormErrorMessage>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" colorScheme="blue">
                {editingCertification ? 'Update' : 'Add'} Certification
              </Button>
            </ModalFooter>
          </Box>
        </ModalContent>
      </Modal>
    </Box>
  )
}
